<script setup>
import { computed, provide, ref, watch } from 'vue';
import { PhxStrapFinder, PhxStrapFinderHeader, PhxStrapFinderSections, useStrapFinder } from '@phx-styleguides/iwc';
import BuckleTypeSection from '@maison/components/strapFinder/sections/BuckleTypeSection.vue';
import StrapStyleSection from '@maison/components/strapFinder/sections/StrapStyleSection.vue';
import StrapSection from '@maison/components/strapFinder/sections/StrapSection.vue';
import StrapSizeSection from '@maison/components/strapFinder/sections/StrapSizeSection.vue';
import { clientApi } from '@phoenix/clientApi';
import BuckleSection from '@maison/components/strapFinder/sections/BuckleSection.vue';
import SummarySection from '@maison/components/strapFinder/sections/SummarySection.vue';
import { PhxsgLoader } from '@sg/src/View/Components/Atoms/Loader';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  reference: {
    type: String,
    required: true,
  },
  buckleTypeSection: {
    type: Object,
    required: true,
  },
  strapStyleSection: {
    type: Object,
    required: true,
  },
  strapSection: {
    type: Object,
    required: true,
  },
  strapSizeSection: {
    type: Object,
    required: true,
  },
  buckleSection: {
    type: Object,
    required: true,
  },
  summarySection: {
    type: Object,
    required: true,
  },
});

const isLoading = ref(false);
const watchName = ref(null);
const straps = ref([]);

const currentBuckleType = ref(null);
const currentStrapStyles = ref([]);
const currentStrap = ref(null);
const currentStrapSize = ref(null);
const currentBuckle = ref(null);

provide('straps', straps);

const { validateSection, ...strapFinderProps } = useStrapFinder([
  'buckle-type',
  'strap-style',
  'strap',
  'strap-size',
  'buckle',
  'summary',
]);
const { t } = useI18n();

provide('sections', strapFinderProps.sections);

const selectedStrap = computed(() => {
  return straps.value.find((strap) => strap.modelReference === currentStrap.value);
});

function onStepValidate(event, stepId, changeMedia) {
  // validate the current section so we can scroll down
  validateSection(stepId);

  if (typeof changeMedia === 'function') {
    // Update the current section media if required
    changeMedia(event.target.value);
  }
}

async function getStrapsFromReference() {
  isLoading.value = true;

  try {
    const requestResult = await clientApi.get(
      window.route('api.strapFinder.getWatchFromReference', { reference: props.reference }),
    );

    const data = requestResult?.data?.data;
    if (!data || !data?.straps || data.straps.length === 0) {
      throw 'No data found in request result';
    }

    watchName.value = data.name;
    straps.value = data.straps;
  } catch {
    window.Phoenix.notification.create(
      t('StrapFinderConfigurator.errors.noStrapAvailableForThisStrap', { reference: props.reference }),
      'error',
    );
    window.location.href = window.route('strap_finder_search_page');
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => currentBuckleType.value,
  () => {
    currentStrapStyles.value = [];
  },
);

watch(
  () => currentStrapStyles.value,
  () => {
    currentStrap.value = null;
  },
);

watch(
  () => currentStrap.value,
  () => {
    currentStrapSize.value = null;
  },
);

watch(
  () => currentStrapSize.value,
  () => {
    currentBuckle.value = null;
  },
);

getStrapsFromReference();
</script>

<template>
  <div v-if="isLoading" class="center center--and-text">
    <PhxsgLoader />
  </div>

  <phx-strap-finder
    v-else
    :sections="strapFinderProps.sections.value"
    :validated-sections="strapFinderProps.validatedSections.value"
    :register-section="strapFinderProps.registerSection"
    :unregister-section="strapFinderProps.unregisterSection"
  >
    <phx-strap-finder-header :watch-name="$t('StrapFinderConfigurator.title', { name: watchName })" :price="''" />

    <phx-strap-finder-sections>
      <buckle-type-section
        v-bind="buckleTypeSection"
        v-model:current-buckle-type="currentBuckleType"
        @on-step-validate="onStepValidate"
      />

      <strap-style-section
        v-bind="strapStyleSection"
        v-model:current-strap-styles="currentStrapStyles"
        :current-buckle-type="currentBuckleType"
        @on-step-validate="onStepValidate"
      />

      <strap-section
        v-bind="strapSection"
        v-model:current-strap="currentStrap"
        :current-buckle-type="currentBuckleType"
        :current-strap-styles="currentStrapStyles"
        @on-step-validate="onStepValidate"
      />

      <strap-size-section
        v-bind="strapSizeSection"
        v-model:current-strap-size="currentStrapSize"
        :selected-strap="selectedStrap"
        @on-step-validate="onStepValidate"
      />

      <buckle-section
        v-if="!selectedStrap?.isSteelStrap"
        v-bind="buckleSection"
        v-model:current-buckle="currentBuckle"
        :selected-strap="selectedStrap"
        @on-step-validate="onStepValidate"
      />

      <summary-section
        v-bind="summarySection"
        :selected-strap="selectedStrap"
        :current-strap-size="currentStrapSize"
        :current-buckle="currentBuckle"
        @on-step-validate="onStepValidate"
      />
    </phx-strap-finder-sections>
  </phx-strap-finder>
</template>
